import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const DEFAULT_STORAGE_LIMIT = 10 * 1024 * 1024; // 10MB

export interface StorageUsageData {
  storageUsed: number;
  storageLimit: number;
  purchasedStorage: number;
  totalStorage: number;
  percentage: number;
  isAtLimit: boolean;
  isNearLimit: boolean;
}

export const useStorageUsage = () => {
  const { user } = useAuth();
  const [storageData, setStorageData] = useState<StorageUsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStorageUsage = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Buscar limite de armazenamento do plano
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('storage_limit')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      const storageLimit = subscription?.storage_limit || DEFAULT_STORAGE_LIMIT;

      // Buscar storage comprado adicional
      const { data: purchasedStorageData } = await supabase
        .from('purchased_storage')
        .select('storage_purchased')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .gte('expires_at', new Date().toISOString());

      const purchasedStorage = purchasedStorageData?.reduce(
        (acc, item) => acc + (item.storage_purchased || 0), 
        0
      ) || 0;

      const totalStorage = storageLimit + purchasedStorage;

      // Buscar uso atual de armazenamento (files + legal_chat_files + caso_kb_links)
      const { data: files } = await supabase
        .from('files')
        .select('size')
        .eq('user_id', user.id);

      const { data: legalFiles } = await supabase
        .from('legal_chat_files')
        .select('file_size')
        .eq('user_id', user.id);

      const { data: casoKBFiles } = await supabase
        .from('caso_kb_links')
        .select('file_size')
        .eq('user_id', user.id);

      const filesSize = files?.reduce((acc, file) => acc + (file.size || 0), 0) || 0;
      const legalFilesSize = legalFiles?.reduce((acc, file) => acc + (file.file_size || 0), 0) || 0;
      const casoKBSize = casoKBFiles?.reduce((acc, file) => acc + (file.file_size || 0), 0) || 0;
      const storageUsed = filesSize + legalFilesSize + casoKBSize;
      const percentage = Math.min((storageUsed / totalStorage) * 100, 100);

      setStorageData({
        storageUsed,
        storageLimit,
        purchasedStorage,
        totalStorage,
        percentage: Math.round(percentage * 100) / 100,
        isAtLimit: percentage >= 100,
        isNearLimit: percentage >= 80
      });
    } catch (error) {
      console.error('Error fetching storage usage:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorageUsage();
  }, [user]);

  // Real-time updates
  useEffect(() => {
    if (!user) return;

    const timestamp = Date.now();

    const filesChannel = supabase
      .channel(`storage-usage-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'files'
        },
        () => {
          fetchStorageUsage();
        }
      )
      .subscribe();

    const subscriptionChannel = supabase
      .channel(`storage-subscription-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchStorageUsage();
        }
      )
      .subscribe();

    const legalFilesChannel = supabase
      .channel(`storage-legal-files-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'legal_chat_files'
        },
        () => {
          fetchStorageUsage();
        }
      )
      .subscribe();

    const casoKBChannel = supabase
      .channel(`storage-caso-kb-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'caso_kb_links'
        },
        () => {
          fetchStorageUsage();
        }
      )
      .subscribe();

    const purchasedStorageChannel = supabase
      .channel(`storage-purchased-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'purchased_storage'
        },
        () => {
          fetchStorageUsage();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(filesChannel);
      supabase.removeChannel(subscriptionChannel);
      supabase.removeChannel(legalFilesChannel);
      supabase.removeChannel(casoKBChannel);
      supabase.removeChannel(purchasedStorageChannel);
    };
  }, [user]);

  const isStorageLimitReached = (): boolean => {
    return storageData ? storageData.isAtLimit : false;
  };

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  return {
    storageData,
    loading,
    isStorageLimitReached,
    formatSize,
    refetch: fetchStorageUsage
  };
};